/**
 * ExplainButton — the ✨ overlay that ships with every chart in Phase 2+.
 *
 * Click the button, a side drawer slides in, and Claude's 2-3 sentence
 * narrative streams in token-by-token via SSE from `/api/explain`.
 *
 * Usage:
 *   renderExplainButton({
 *     label:   "Explain this drift",
 *     context: () => `Calibration heatmap...\n\nLast 30d:\n${heatmapAsCsv}`,
 *   })
 *
 * The drawer is global (one per page) — multiple buttons share it.
 * Mount renderExplainDrawer() once in App.ts.
 */
import { html, render, type TemplateResult } from "lit-html";

interface ExplainContext {
  label:   string;                 // button label, e.g. "Explain this spike"
  context: () => string;           // lazy: built at click time so it's fresh
  system?: string;                 // optional override of the default system prompt
}

// Drawer state — single global instance (only one stream at a time).
const drawerState = {
  open:     false,
  loading:  false,
  title:    "",
  text:     "",
  error:    null as Error | null,
  abort:    null as AbortController | null,
  paint:    () => {},              // re-render hook, set by drawer mount
};

// =============================================================
// Button — drop into any chart panel
// =============================================================
export function renderExplainButton(ctx: ExplainContext): TemplateResult {
  return html`
    <button
      class="explain-btn"
      title="${ctx.label}"
      @click=${() => openDrawer(ctx)}
    >
      <span class="explain-btn__icon">✨</span>
      <span class="explain-btn__label">${ctx.label}</span>
    </button>
  `;
}

// =============================================================
// Drawer — global, mount once in App.ts
// =============================================================
export function renderExplainDrawer(root: HTMLElement): void {
  drawerState.paint = () => render(drawerTemplate(), root);
  drawerState.paint();
}

function drawerTemplate(): TemplateResult {
  if (!drawerState.open) return html``;

  return html`
    <div class="explain-drawer-backdrop"
         @click=${(e: MouseEvent) => {
           if (e.target === e.currentTarget) closeDrawer();
         }}>
      <aside class="explain-drawer glass">
        <header class="explain-drawer__head">
          <span class="explain-drawer__icon">✨</span>
          <h2 class="explain-drawer__title">${drawerState.title}</h2>
          <button class="explain-drawer__close"
                  @click=${closeDrawer}
                  title="Close (Esc)">×</button>
        </header>
        <div class="explain-drawer__body">
          ${drawerState.error
            ? errorView(drawerState.error)
            : drawerState.text
              ? html`<div class="explain-drawer__text">${renderMarkdown(drawerState.text)}</div>${
                  drawerState.loading ? html`<span class="explain-drawer__caret"></span>` : ""
                }`
              : drawerState.loading
                ? html`<div class="explain-drawer__loading">
                    <span class="explain-drawer__shimmer"></span>
                    <span class="explain-drawer__shimmer"></span>
                    <span class="explain-drawer__shimmer"
                          style="width:60%;"></span>
                  </div>`
                : html`<div class="explain-drawer__placeholder">
                    no narrative yet
                  </div>`}
        </div>
        <footer class="explain-drawer__foot">
          <span style="color:var(--text-quaternary);font-size:11px;">
            sonnet 4.6 · streaming
          </span>
          ${drawerState.loading ? html`
            <button class="explain-drawer__cancel"
                    @click=${cancelStream}>cancel</button>
          ` : ""}
        </footer>
      </aside>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="explain-drawer__error">
      <div style="font-weight:500;color:var(--accent-red-light);
                   margin-bottom:6px;">
        ${err.message}
      </div>
      ${err.message.includes("anthropic_not_configured") ? html`
        <div style="font-size:12px;color:var(--text-tertiary);
                     line-height:1.5;font-family:monospace;
                     background:var(--bg-glass-thin);
                     padding:10px;border-radius:8px;margin-top:8px;">
          cd cloudflare && wrangler secret put ANTHROPIC_API_KEY
        </div>
      ` : ""}
    </div>
  `;
}

// =============================================================
// Stream control
// =============================================================
function openDrawer(ctx: ExplainContext): void {
  cancelStream();
  drawerState.open    = true;
  drawerState.loading = true;
  drawerState.title   = ctx.label;
  drawerState.text    = "";
  drawerState.error   = null;
  drawerState.paint();
  void runStream(ctx);
}

function closeDrawer(): void {
  cancelStream();
  drawerState.open = false;
  drawerState.paint();
}

function cancelStream(): void {
  if (drawerState.abort) drawerState.abort.abort();
  drawerState.abort   = null;
  drawerState.loading = false;
}

async function runStream(ctx: ExplainContext): Promise<void> {
  const ac = new AbortController();
  drawerState.abort = ac;

  try {
    const r = await fetch("/api/explain", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        context: ctx.context(),
        system:  ctx.system,
      }),
      signal: ac.signal,
      redirect: "error",
    });

    if (!r.ok) {
      const errBody = await r.text().catch(() => "");
      throw new Error(`/api/explain → ${r.status}: ${errBody.slice(0, 200)}`);
    }
    if (!r.body) throw new Error("no_response_body");

    // Parse Anthropic SSE stream.
    // Frames look like:
    //   event: content_block_delta
    //   data: {"type":"content_block_delta","index":0,"delta":{"type":"text_delta","text":"..."}}
    const reader  = r.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";        // last partial stays in buffer
      for (const frame of frames) {
        const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        try {
          const obj = JSON.parse(dataLine.slice(5).trim());
          if (obj.type === "content_block_delta" && obj.delta?.type === "text_delta") {
            drawerState.text += obj.delta.text as string;
            drawerState.paint();
          }
        } catch {
          // Ignore unparseable frames (heartbeats, etc.)
        }
      }
    }
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
    drawerState.error = e instanceof Error ? e : new Error(String(e));
  } finally {
    drawerState.loading = false;
    drawerState.abort   = null;
    drawerState.paint();
  }
}

// =============================================================
// Tiny markdown renderer — bold / italic / inline code / line breaks.
// Anything fancier and we'll pull marked.js (~30KB), but Claude's
// 2-3 sentence outputs almost never need more than this.
// =============================================================
function renderMarkdown(text: string): TemplateResult[] {
  const lines = text.split("\n");
  return lines.map((line) => {
    const segments: TemplateResult[] = [];
    let i = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > lastIdx) {
        segments.push(html`${line.slice(lastIdx, m.index)}`);
      }
      const tok = m[0];
      if (tok.startsWith("**")) {
        segments.push(html`<strong>${tok.slice(2, -2)}</strong>`);
      } else if (tok.startsWith("*")) {
        segments.push(html`<em>${tok.slice(1, -1)}</em>`);
      } else {
        segments.push(html`<code>${tok.slice(1, -1)}</code>`);
      }
      lastIdx = re.lastIndex;
      i++;
    }
    if (lastIdx < line.length) segments.push(html`${line.slice(lastIdx)}`);
    void i;
    return html`<div class="explain-drawer__line">${segments}</div>`;
  });
}

// =============================================================
// Esc-to-close — wired once on first import
// =============================================================
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerState.open) {
      e.preventDefault();
      closeDrawer();
    }
  });
}
